// Componente responsável pela tela de desvinculação de perfil
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState, useCallback } from "react"; // Adicionando useState e useCallback
import H1 from "../../../../../components/UI/Titles/H1/h1";
import RoundedButton from "../../../../../components/Buttons/RoundedButton";
import TagInput from "../../../../../components/UI/Inputs/TagInput";
import StateView from '../shared/StateView';
import LinkMessage from '../shared/LinkMessage';
import classes from '../../RescueCode.module.scss';

/**
 * Componente que renderiza a tela de desvinculação de perfil
 * 
 * @param {Object} currentParentUser - Dados do usuário profissional ao qual o usuário está vinculado
 * @param {string} unlinkInput - Valor atual do campo de texto para confirmar a desvinculação
 * @param {string} unlinkError - Mensagem de erro a ser exibida (se houver)
 * @param {boolean} showUnlinkConfirm - Controla a exibição dos botões de confirmação
 * @param {Function} updateState - Função para atualizar o estado no componente pai
 * @param {Function} confirmUnlink - Função que executa a desvinculação na API
 * @param {Function} cancelUnlinkConfirm - Função para cancelar a confirmação
 * @param {Function} redirectToDashboard - Função para redirecionar ao painel
 */
const UnlinkProfileView = ({ 
  currentParentUser, 
  unlinkInput, 
  unlinkError, 
  showUnlinkConfirm,
  updateState, 
  confirmUnlink, 
  cancelUnlinkConfirm,
  redirectToDashboard
}) => {
  // Estado local para armazenar o valor do input sem atualizar o estado global a cada digitação
  const [localInput, setLocalInput] = useState(unlinkInput);

  // Handler para atualização do input local
  const handleInputChange = useCallback((event) => {
    // Apenas atualiza o estado local do input, convertendo para maiúsculas
    setLocalInput(event.target.value.toUpperCase());
  }, []);

  // Função para validar e atualizar o estado global apenas quando necessário
  const handleUnlinkButtonClick = useCallback(() => {
    // Atualiza o estado global com o valor atual do input
    updateState({ unlinkInput: localInput });
    
    // Validação do texto digitado
    if (localInput === "DESVINCULAR") {
      // Se correto, exibe a confirmação final
      updateState({ showUnlinkConfirm: true });
    } else {
      // Se incorreto, exibe mensagem de erro
      updateState({ 
        unlinkError: "É necessário digitar DESVINCULAR exatamente como mostrado." 
      });
    }
  }, [localInput, updateState]);

  // Função para limpar o erro ao focar no input
  const handleInputFocus = useCallback(() => {
    if (unlinkError) {
      updateState({ unlinkError: "" });
    }
  }, [unlinkError, updateState]);

  return (
    <StateView className={classes.unlinkContainer}>
      {/* Título principal da tela */}
      <H1 className={classes.unlinkTitle}>Desvincular perfil</H1>

      {/* Mensagem explicativa com instruções para o usuário */}
      <LinkMessage className={classes.unlinkMessage}>
        <p>Para desvincular seu perfil de <strong>{currentParentUser?.given_name || "o profissional"}</strong>, 
           digite <strong>DESVINCULAR</strong> no campo abaixo e confirme.</p>
        
        {/* Aviso de consequências da desvinculação */}
        <div className={classes.warningText}>
          <FontAwesomeIcon icon={faExclamationTriangle} /> Atenção: ao desvincular seu perfil, você não terá mais acesso às 
          atividades que o profissional adicionou para você, e ele não poderá mais 
          ver suas atividades.
        </div>
      </LinkMessage>

      {/* Campo de input para digitação da palavra "DESVINCULAR" */}
      <div className={classes.unlinkInputArea}>
        <TagInput
          centerText
          placeholder="Digite DESVINCULAR"
          className={classes.unlinkInput}
          value={localInput}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
        />
        {/* Exibe mensagem de erro, se houver */}
        {unlinkError && (
          <div className={classes.errorMessage}>{unlinkError}</div>
        )}
      </div>

      {/* Renderização condicional dos botões baseada no estado de confirmação */}
      {showUnlinkConfirm ? (
        // Botões de confirmação final (exibidos após o usuário digitar "DESVINCULAR")
        <div className={classes.buttonGroup}>
          <RoundedButton
            className={classes.rejectButton}
            text="CANCELAR"
            onClick={cancelUnlinkConfirm}
          />
          <RoundedButton
            className={classes.dangerButton}
            text="CONFIRMAR"
            onClick={confirmUnlink}
          />
        </div>
      ) : (
        // Botão inicial de desvinculação - agora verifica apenas visualmente se deve estar habilitado
        <RoundedButton
          className={classes.dangerButton}
          text="DESVINCULAR PERFIL"
          onClick={handleUnlinkButtonClick}
          disabled={localInput !== "DESVINCULAR"}
        />
      )}

      {/* Botão para voltar à tela anterior */}
      <RoundedButton
        className={classes.returnButton}
        text="VOLTAR"
        onClick={() => updateState({ current: "NO_INVITE" })}
      />
    </StateView>
  );
};

export default UnlinkProfileView;